import type { ReactNode } from "react";
import { ClampedText } from "../../../common/components/TruncatedText/ClampedText";

export interface StatsCardVisualProps {
  title: string;
  subtitle?: string;
  thumbnailUrl?: string;
  badge?: {
    text: string;
    variant:
      | "success"
      | "warning"
      | "error"
      | "info"
      | "neutral"
      | "sessionLow"
      | "sessionMedium"
      | "sessionHigh";
  };
  onClick?: () => void;
  footer?: ReactNode;
  className?: string;
  compact?: boolean;
}

const badgeStyles = {
  success: "bg-green-100 text-green-700",
  warning: "bg-yellow-100 text-yellow-700",
  error: "bg-red-100 text-red-700",
  info: "bg-blue-100 text-blue-700",
  neutral: "bg-gray-100 text-gray-700",
  sessionLow: "bg-gray-200 text-gray-700",
  sessionMedium: "bg-green-100 text-green-700",
  sessionHigh: "bg-green-200 text-green-800",
};

const StatsCardVisual = ({
  title,
  subtitle,
  thumbnailUrl,
  badge,
  onClick,
  footer,
  className = "",
  compact = false,
}: StatsCardVisualProps) => {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all ${
        onClick ? "cursor-pointer hover:border-green-300" : ""
      } ${className}`}
      style={compact ? { width: "180px" } : undefined}
    >
      <div className={`relative ${compact ? "h-24" : "h-40"} bg-gray-100`}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-2 right-2 w-16 h-16 border-2 border-white rounded-full" />
              <div className="absolute bottom-4 left-4 w-8 h-8 border-2 border-white rounded-full" />
              <div className="absolute top-1/2 left-1/3 w-4 h-4 bg-white rounded-full" />
            </div>
          </div>
        )}

        {badge && (
          <div className={`absolute ${compact ? "top-2 right-2" : "top-3 right-3"}`}>
            <span
              className={`${
                compact ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"
              } font-medium rounded-full ${badgeStyles[badge.variant]}`}
            >
              {badge.text}
            </span>
          </div>
        )}

        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent ${
            compact ? "p-2 pt-6" : "p-4 pt-10"
          }`}
        >
          <ClampedText
            as="h3"
            className={`text-white font-semibold ${compact ? "text-sm" : "text-lg"}`}
            style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.5)" }}
            text={title}
          />
        </div>
      </div>

      {(subtitle || footer) && (
        <div className={compact ? "p-2" : "p-4"}>
          {subtitle && (
            <p className={`${compact ? "text-xs" : "text-sm"} text-gray-500`}>{subtitle}</p>
          )}
          {footer && <div className={compact ? "mt-2" : "mt-3"}>{footer}</div>}
        </div>
      )}
    </div>
  );
};

export default StatsCardVisual;
